import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  InputAdornment,
  Chip,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { getCountryFlag } from '../../utils/countryFlags';
import { supabase } from '../../services/supabase';

interface PricingEditorProps {
  open: boolean;
  onClose: () => void;
  contentId: string;
  schemaUrl: string;
  onSave?: () => void;
}

interface PriceData {
  country: string;
  currency: string;
  price: number;
  priceRange?: string;
}

const CURRENCY_MAP: Record<string, { currency: string; symbol: string }> = {
  'UK': { currency: 'GBP', symbol: '£' },
  'US': { currency: 'USD', symbol: '$' },
  'COM': { currency: 'USD', symbol: '$' },
  'DE': { currency: 'EUR', symbol: '€' },
  'FR': { currency: 'EUR', symbol: '€' },
  'IT': { currency: 'EUR', symbol: '€' },
  'ES': { currency: 'EUR', symbol: '€' },
  'NL': { currency: 'EUR', symbol: '€' },
  'FI': { currency: 'EUR', symbol: '€' },
  'DK': { currency: 'DKK', symbol: 'kr' },
  'SE': { currency: 'SEK', symbol: 'kr' },
  'NO': { currency: 'NOK', symbol: 'kr' },
  'AU': { currency: 'AUD', symbol: '$' },
  'CA': { currency: 'CAD', symbol: '$' },
  'BR': { currency: 'BRL', symbol: 'R$' },
  'JP': { currency: 'JPY', symbol: '¥' },
  'KR': { currency: 'KRW', symbol: '₩' },
  'CN': { currency: 'CNY', symbol: '¥' },
  'RU': { currency: 'RUB', symbol: '₽' }
};

const PricingEditor: React.FC<PricingEditorProps> = ({
  open,
  onClose,
  contentId,
  schemaUrl,
  onSave
}) => {
  const [prices, setPrices] = useState<Record<string, PriceData>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [basePrice, setBasePrice] = useState<number>(55); // USD base price

  useEffect(() => {
    if (open && contentId) {
      loadPricing();
    }
  }, [open, contentId]);

  const loadPricing = async () => {
    try {
      setLoading(true);
      // Load existing pricing from the backend
      const { data: { session: pricingSession } } = await supabase.auth.getSession();
      const response = await fetch(`/api/schema/pricing/${contentId}`, {
        headers: pricingSession?.access_token
          ? { Authorization: `Bearer ${pricingSession.access_token}` }
          : {},
      });
      if (response.ok) {
        const data = await response.json();
        if (data.prices) {
          const priceMap: Record<string, PriceData> = {};
          data.prices.forEach((p: any) => {
            priceMap[p.country] = {
              country: p.country,
              currency: p.currency,
              price: p.price,
              priceRange: p.price_range
            };
          });
          setPrices(priceMap);
        }
      }
    } catch (err) {
      console.error('Error loading pricing:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (country: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setPrices(prev => ({
      ...prev,
      [country]: {
        ...prev[country],
        country,
        currency: CURRENCY_MAP[country].currency,
        price: numValue
      }
    }));
  };

  const handleAutoConvert = () => {
    // Auto-convert from USD base price using rough exchange rates
    const exchangeRates: Record<string, number> = {
      'GBP': 0.82,
      'EUR': 0.91,
      'DKK': 6.82,
      'SEK': 10.0,
      'NOK': 10.0,
      'AUD': 1.55,
      'CAD': 1.36,
      'BRL': 5.0,
      'JPY': 145,
      'KRW': 1270,
      'CNY': 6.9,
      'RUB': 82
    };

    const newPrices: Record<string, PriceData> = {};
    
    Object.entries(CURRENCY_MAP).forEach(([country, { currency, symbol }]) => {
      let convertedPrice = basePrice;
      
      if (currency !== 'USD') {
        const rate = exchangeRates[currency] || 1;
        convertedPrice = Math.round(basePrice * rate);
        
        // Round to nice numbers
        if (currency === 'JPY' || currency === 'KRW') {
          convertedPrice = Math.round(convertedPrice / 1000) * 1000;
        } else if (currency === 'DKK' || currency === 'SEK' || currency === 'NOK') {
          convertedPrice = Math.round(convertedPrice / 25) * 25;
        } else if (convertedPrice > 100) {
          convertedPrice = Math.round(convertedPrice / 5) * 5;
        }
      }
      
      newPrices[country] = {
        country,
        currency,
        price: convertedPrice,
        priceRange: `${symbol}${convertedPrice}-${Math.round(convertedPrice * 1.6)}`
      };
    });
    
    setPrices(newPrices);
    setSuccess('Prices auto-converted from USD base price');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const { data: { session: saveSession } } = await supabase.auth.getSession();
      const response = await fetch('/api/schema/pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(saveSession?.access_token ? { Authorization: `Bearer ${saveSession.access_token}` } : {}),
        },
        body: JSON.stringify({
          contentId,
          prices: Object.values(prices)
        })
      });
      
      if (response.ok) {
        setSuccess('Pricing saved successfully!');
        onSave?.();
        setTimeout(() => onClose(), 1500);
      } else {
        setError('Failed to save pricing');
      }
    } catch (err: any) {
      setError(err.message || 'Error saving pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyFromCountry = (sourceCountry: string) => {
    const sourcePrice = prices[sourceCountry];
    if (sourcePrice) {
      // Copy to all countries with the same currency
      const sourceCurrency = CURRENCY_MAP[sourceCountry].currency;
      const newPrices = { ...prices };
      
      Object.entries(CURRENCY_MAP).forEach(([country, { currency }]) => {
        if (currency === sourceCurrency && country !== sourceCountry) {
          newPrices[country] = {
            ...sourcePrice,
            country
          };
        }
      });
      
      setPrices(newPrices);
      setSuccess(`Copied ${sourceCurrency} pricing to all ${sourceCurrency} markets`);
    }
  };

  // Group countries by currency
  const currencyGroups: Record<string, string[]> = {};
  Object.entries(CURRENCY_MAP).forEach(([country, { currency }]) => {
    if (!currencyGroups[currency]) {
      currencyGroups[currency] = [];
    }
    currencyGroups[currency].push(country);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MoneyIcon color="primary" />
        Pricing Manager
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Managing pricing for: <strong>{schemaUrl}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Content ID: {contentId}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 6 }}>
              <TextField
                label="USD Base Price"
                type="number"
                value={basePrice}
                onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Button
                variant="outlined"
                onClick={handleAutoConvert}
                fullWidth
                startIcon={<MoneyIcon />}
              >
                Auto-Convert All
              </Button>
            </Grid>
          </Grid>
        </Box>

        {Object.entries(currencyGroups).map(([currency, countries]) => (
          <Paper key={currency} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {currency} Markets
              </Typography>
              {countries.length > 1 && prices[countries[0]] && (
                <Tooltip title={`Copy ${currency} price to all ${currency} markets`}>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyFromCountry(countries[0])}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            
            <Grid container spacing={2}>
              {countries.map(country => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={country}>
                  <TextField
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{getCountryFlag(country)}</span>
                        <span>{country}</span>
                      </Box>
                    }
                    type="number"
                    value={prices[country]?.price || ''}
                    onChange={(e) => handlePriceChange(country, e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {CURRENCY_MAP[country].symbol}
                        </InputAdornment>
                      )
                    }}
                    fullWidth
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> Use "Auto-Convert All" to quickly set prices based on current exchange rates, 
            then adjust individual markets as needed. Prices will be automatically included when translating schemas.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || Object.keys(prices).length === 0}
          startIcon={<SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Pricing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingEditor;